import { APP_KEYS } from 'services/appInstallStatus/constants'
import { GENDER_VALUE } from 'models/ProfileInfo/constants'
import { PROFILE_REWARDS_CATEGORIES } from 'models/ProfileRewards/constants'

import { CRYPTO_KEYS, PENDING_NOTIFICATION_MODAL_TYPE } from './constants'

export const STRING = {
  emDash: '—',
  deprecationNotice: {
    header: 'Take back control now —',
    title: {
      part1: 'GET REKLAIM ON YOUR ',
      part2: 'MOBILE DEVICE',
    },
  },
  invalidPostalCodeTitle: (postalCodeDisplay) => `Invalid ${postalCodeDisplay}`,
  invalidPostalCodeMessage: (region, postalCodeDisplay, postalCodeFormat) =>
    `Looks like you have put in a wrong ${region} ${postalCodeDisplay} format. Please check to make sure you have the correct format, i.e. ${postalCodeFormat}`,
  invalidGenderMessage: 'Select a valid gender',
  backToReklaimButtonMessage: 'Back to reklaimyours.com',
  units: {
    point: 'POINT',
    points: 'POINTS',
    pointShort: 'PT',
    pointsShort: 'PTS',
  },
  navMenu: {
    logoAlt: 'Reklaim',
    openMenuAlt: 'Open Nav Menu',
    closeMenuAlt: 'Close Nav Menu',
    balanceLabel: 'My balance',
    status: {
      basic: 'Basic Reklaim Account',
      trusted: 'Trusted Reklaim Account',
      vip: 'VIP Reklaim Account',
    },
    navMenuItems: {
      reklaimProtect: 'Reklaim Protect',
      redeem: 'Redeem',
      paypal: 'PayPal',
      venmo: 'Venmo',
      giftcard: 'Gift Cards',
    },
    rafCard: {
      title: (bonusPoints) => `RECEIVE ${bonusPoints} POINTS PER FRIEND`,
      startReferring: 'Start referring',
    },
    accountLink: 'Account',
    signOutButton: 'Sign out',
  },
  footer: {
    copyrightText: (year) => `© ${year} Reklaim. All Rights Reserved.`,
    privacyPolicy: 'Privacy Policy',
    earn: 'Earn',
    noticeOfIncentive: 'Notice of Incentive',
    linkSplitter: '/',
    reklaimLogoAlt: 'Reklaim',
  },
  intercom: {
    messengerButtonAlt: 'Support',
  },
  signOut: {
    title: "We're logging you out...",
    message: "If you think you shouldn't be, please reach out to us!",
  },
  latentActivation: {
    invalidAccount: {
      title: 'WELL THIS IS AWKWARD!',
      message1: "Unfortunately, we're having a problem setting up your account.",
      message2:
        'Please try again, and if you are still having issues, please reach out to our Customer Support team at ',
      goToHomePage: 'Go to the homepage',
    },
    activeAccount: {
      title: 'YOUR ACCOUNT IS ALREADY SET UP!',
      message:
        'Good news, it looks like your account is already set up. All you need to do now is log into your Reklaim account!',
      logIn: 'Log into Reklaim',
    },
    activateModal: {
      closeButtonAlt: 'Close',
      title: 'ENTER YOUR PASSWORD',
      emailHeader: 'Please enter your password for ',
      passwordPlaceholder: 'Password',
      setMyPassword: 'Set my password',
      errorMessage: 'Something went wrong. Please try again later.',
      validation: {
        title: 'Your password must',
        passwordLength: 'At least 8 characters',
        needsNumber: '1 number',
        needsSpecialChar: '1 special character (except = and +)',
        needsLowerAndUpper: '1 lowercase & 1 uppercase',
      },
      successTitle1: 'YOUR ACCOUNT IS NOW SET UP!',
      successMessage: "That's it, you're now ready to use Reklaim!",
      successLogIn: 'Log into Reklaim',
    },
  },
  appGeneralInfo: {
    appInfo: (region) => `App Information: ${region || 'No region'}`,
  },
  signInPage: {
    welcomeTitleHeader: "It's your data —",
    welcomeTitle: {
      part1: 'ACCESS WHAT ',
      part2: 'THEY ',
      part3: 'HAVE',
    },
    loginButtonText: 'Log in',
    placeholderUsername: 'Email/Phone Number',
    invalidUsernameMessage: 'Please enter a valid email or phone number',
    unverifiedCheckErrorMessage: 'Something went wrong. Please try again later.',
    cognitoErrorUserNotFound: 'Invalid username.',
    cognitoErrorDefault: 'Something went wrong. Please try again later.',
  },
  signInCodeVerification: {
    title: 'ENTER YOUR SIGN IN CODE',
    message: "We've sent your sign in code to:",
    resendButton: 'Resend sign in code',
    resendFailed: 'Could not resend the code. Please try again later.',
    resendSent: 'Sent! Please check for a new code',
    logInButton: 'Log in',
    cognitoErrorFailedToConfirmSignIn: 'Failed to sign in. Please confirm your verification code and try again.',
    cognitoErrorInternalError: {
      email:
        'The link you used has expired, we have sent you a new one. Please check your email for the new link to login.',
      sms: 'The link you used has expired, we have sent you a new one. Please check your sms for the new link to login.',
    },
  },
  unverifiedEmail: {
    title: 'YOUR EMAIL IS NOT VERIFIED',
    text: 'Oops, looks like the email you are using to login has not been verified, please click below to send a verification code and login.',
    resend: 'Resend',
    noThanks: 'No thanks',
  },
  unverifiedPhoneNumber: {
    title: 'YOUR PHONE NUMBER IS NOT VERIFIED',
    text: 'Oops, looks like the phone number you are using to login has not been verified, please click below to send a verification code and login.',
    resend: 'Resend',
    noThanks: 'No thanks',
  },
  unveilSignUp: {
    summaryScreen: {
      summaryTitle: "YOUR DATA ISN'T YOURS...",
      passwordsCount: 'Exposed passwords',
      passwordsCountToolTip:
        'An exposed password is when we detect that one of your passwords has been breached from a service/company you signed up for.',
      sourcesCount: 'Sources holding your data',
      sourcesCountToolTip:
        'A source is a company or organization that we detected to have access to and benefit from your personal data.',
      identifiersCount: 'Identifiers',
      identifiersCountToolTip:
        'An identifier is an email address or phone number that has been associated to your data profile by brokers.',
      brokersCount: 'Brokers data points',
      brokersTooltip:
        'When a piece of your personal information has been collected and aggregated into a data profile by brokers.',
      breachedDataCount: 'Breached data points',
      breachedTooltip:
        'When you provide your personal information to a company, but an unauthorized person gained access though malicious actions.',
      profileFoundTitle: {
        titleIdentifier: (identifier) => `${identifier} `,
        title: 'HAS BEEN EXPOSED.',
      },
      attributesCategory: {
        passwords: 'Passwords',
        personalDataPoints: 'Personal data points',
        breaches: 'Breaches',
        dataBrokers: 'Data brokers',
      },
      learnMoreExplainerModal: {
        passwords: {
          label: 'PASSWORDS',
          description:
            "The amount of passwords that have been breached from any online service/product that you've created an account with.",
        },
        personalDataPoints: {
          label: 'PERSONAL DATA POINTS',
          description:
            'Information related to you that has been found in data breaches or broker profiles such as your address, name, income, occupation, credit card info, etc.',
        },
        breachesCount: {
          label: 'BREACHES',
          description:
            'A breach is when an unauthorized person gains access to your personal information from a company or organization.',
        },
        dataBrokersCount: {
          label: 'DATA BROKERS',
          description:
            'A data broker is a company that collects, crawls the internet (legally or otherwise), and aggregates your data and sells it.',
        },
      },
      learnMoreButton: 'Learn More',
      continueButton: 'Show me my data',
    },
  },
  welcomePage: {
    title: "It's your data.",
    subtitle: 'Take back control of it',
    features: {
      protect: {
        title: 'Protect my data',
        subtitle: 'Scan for your exposed data and protect it',
      },
      earn: {
        title: 'Earn from my data',
        subtitle: 'Join our data earnings program',
      },
    },
  },
  signUpPage: {
    privacy: {
      title: {
        part1: 'CHECK IF YOUR DATA HAS BEEN ',
        part2: 'EXPOSED',
        part3: '.',
      },
      buttonText: 'Scan',
    },
    compensation: {
      title: {
        line1: {
          part1: 'JOIN ',
          part2: 'REKLAIM',
          part3: ',',
        },
        line2: "TAKE BACK WHAT'S YOURS.",
      },
      description: 'Enter your email or phone number to get started.',
      buttonText: 'Continue',
    },
    usernamePlaceholder: 'Enter email or phone number',
    invalidUsernameMessage: 'Please enter a valid email or phone number',
    acknowledgeText: 'I acknowledge that this is a complimentary service because Reklaim cares about consumer privacy.',
    buttonText: 'Take it back',
    existingUserModal: {
      title: 'EMAIL ADDRESS OR PHONE NUMBER ALREADY IN USE',
      message:
        'An account with that email or phone number already exists, please try a different email address or phone number, or log in to your account.',
      genericTitle: "Oops, looks like you've already signed up!",
      buttonTitle: 'Login',
    },
  },
  explainerSignUp: {
    dataCollected: '300+',
    dataCollectedLabel: 'Pieces of data collected from you every day',
    dataBreaches: '155M+',
    dataBreachesLabel: "Individuals' data has been breached in the past year",
    dataBrokers: '500+',
    dataBrokersLabel: 'Data brokers making profit from your data',
  },
  reklaimPrivacy: {
    header: 'PRIVACY',
    title: 'PROTECT YOUR DIGITAL PRIVACY.',
    description: 'Know when and what has been exposed in breaches and more with Reklaim Privacy.',
    breachDetection: {
      label: 'Breach notifications',
      description: 'Know when your sensitive data is exposed online.',
    },
    dataBrokerDetection: {
      label: 'Data broker detection',
      description: 'See what information is being collected from you.',
    },
    unlimitedVPN: {
      label: 'Unlimited VPN',
      description: 'Browse the web more securely with our VPN.',
    },
    continueButton: 'Continue',
  },
  signUpPhoneNumberVerification: {
    title: 'VERIFY YOUR PHONE NUMBER.',
    message: "We've sent a verification code to ",
    resendMessage: 'Resend verification code',
    resendingMessage: 'Sending...',
    resendingFailedMessage: 'Something went wrong. Please try again later.',
    resentMessage: 'Sent! Please check your inbox',
    verifyButton: 'Verify',
    errorMessage: 'The code provided is invalid. Please try again.',
  },
  unveilScanningScreen: {
    defaultTitle: {
      part1: 'SCANNING FOR YOUR ',
      part2: 'DATA',
      part3: '...',
    },
    fullUnveilOnboardingTitle: {
      part1: 'FETCHING THE REST OF YOUR ',
      part2: 'DATA',
      part3: '...',
    },
    explainerItems: {
      part1: 'Scanning 1M+ reported breaches',
      part2: 'Scanning broker profiles',
      part3: 'Searching for exposed passwords',
    },
  },
  unveilBreachNotificationExplainerScreen: {
    title: {
      part1: '14+ DATA BREACHES ',
      part2: 'BEING REPORTED EACH WEEK.',
    },
    description: 'Reklaim Privacy will monitor and notify you if your data has been compromised in any breaches.',
    enableNotificationsButtonText: 'Notify me',
    skipButtonText: 'Not now',
  },
  unveilVPNExplainerScreen: {
    title: 'BROWSE WITH GREATER PRIVACY USING OUR VPN.',
    perks: {
      publicSecurity: {
        heading: 'Security on public WiFi',
        message: 'Encrypt your traffic to block attackers from accessing sensitive information.',
      },
      privacyFromISP: {
        heading: 'Privacy from your ISP',
        message: 'Your Internet service provider can see your browsing history and sell it to advertisers.',
      },
      privacyFromApps: {
        heading: 'Privacy from apps & websites',
        message: 'Apps like Facebook, Twitter, etc. collect huge amounts of data on you.',
      },
    },
    enableVPNButtonText: 'Enable VPN',
    skipButtonText: 'Not now',
  },
  selectAssociatedIdsModal: {
    title: 'WE DETECTED ADDITIONAL IDENTIFIERS ASSOCIATED TO YOU',
    message: 'Please select all that are still currently in use:',
    selectButton: 'I currently use these',
    unselectAllButton: "I don't use any of these",
  },
  account: {
    subscriptionStatusCard: {
      idsCounterLabel: (availableIdsCount) =>
        `You can add ${availableIdsCount} more ID${availableIdsCount > 1 ? 's' : ''}`,
      idsCounterLimitReachedLabel: {
        basic: 'Upgrade to add more IDs!',
        lite: 'Upgrade to add more IDs!',
        pro: 'ID limit reached!',
      },
      countOfText: ' of ',
      upgradeOptionCTAButton: 'See upgrade options',
    },
    myProfileSection: {
      userHandle: {
        emptyLabel: '—',
        addLabel: 'Add your handle',
        changeLabel: 'Change your handle',
        userHandleAddModal: {
          title: 'YOUR HANDLE',
          description: 'Used for display purposes only.',
          saveButton: 'Save',
          errorMessages: {
            handleCharMinimum: 'Must be at least 4 characters.',
            handleCharMaximum: "Can't exceed 15 characters.",
            handleTaken: 'Handle taken, please try another.',
            handleInvalid: 'Please enter a valid user handle.',
            sameHandleError:
              'This user handle is already linked to your account. Please enter a different user handle.',
            defaultError: 'Something went wrong. Please try again later.',
          },
        },
      },
    },
    identifiersSection: {
      advertisingIdLabel: 'ADVERTISING ID',
      advertisingIdTooltip:
        'Advertising ID is an anonymous, auto-generated (by your device) device identifier. It is used for advertising purposes and is resettable by you.',
      emailAddressesLabel: 'EMAIL ADDRESSES',
      addEmailAddress: 'Add email address',
      emailAddressLimitReached: 'Email address limit reached',
      phoneNumberLabel: 'PHONE NUMBERS',
      addPhoneNumber: 'Add phone number',
      phoneNumberLimitReached: 'Phone number limit reached',
      notSharedPlaceholder: '—',
      makePrimary: 'Make primary',
      verifyButton: 'Verify',
      setPrimaryEmailAddressError: {
        invalidEmailAddressMessage: 'The requested email address is invalid.',
        cognitoPoolEmailAddressError: 'Please re-login to set the requested email address to primary',
        emailAddressNotFoundError: 'The requested email address was not found.',
        identifierPrimaryEmailAddressError: 'The requested email address is a primary email address.',
        defaultErrorMessage: 'Something went wrong. Please try again later.',
      },
      setPrimaryPhoneNumberError: {
        invalidPhoneNumberMessage: 'The requested phone number is invalid.',
        cognitoPoolPhoneNumberError: 'Please re-login to set the requested phone number to primary',
        phoneNumberNotFoundError: 'The requested phone number was not found.',
        identifierPrimaryPhoneNumberError: 'The requested phone number is a primary email address.',
        defaultErrorMessage: 'Something went wrong. Please try again later.',
      },
      primary: 'Primary',
      scanButton: 'Scan for data',
      signOutButton: 'Sign out',
    },
    removePhoneNumberModal: {
      removeTitle: 'CONFIRM REMOVE',
      removeMessage: 'Are you sure you want to remove ',
      questionMark: '?',
      primaryIdRemovalWarningMessage: ' You will no longer be able to log in with this identifier.',
      cancelButton: "Don't remove",
      removeButton: 'Remove',
      cognitoPoolPhoneNumberError: 'Please re-login to remove the requested phone number.',
      phoneNumberNotFoundError: 'The requested phone number was not found.',
      identifierPrimaryPhoneNumberError: 'The requested phone number cannot be removed.',
      defaultErrorMessage: 'Something went wrong. Please try again later.',
      invalidPhoneNumberMessage: 'The requested phone number is invalid.',
    },
    removeEmailAddressModal: {
      removeTitle: 'CONFIRM REMOVE',
      removeMessage: 'Are you sure you want to remove ',
      questionMark: '?',
      primaryIdRemovalWarningMessage: ' You will no longer be able to log in with this identifier.',
      cancelButton: "Don't remove",
      removeButton: 'Remove',
      cognitoPoolEmailAddressError: 'Please re-login to remove the requested email address.',
      emailAddressNotFoundError: 'The requested email address was not found.',
      identifierPrimaryEmailAddressError: 'The requested email address cannot be removed.',
      defaultErrorMessage: 'Something went wrong. Please try again later.',
      invalidEmailAddressMessage: 'The requested email address is invalid.',
    },
    banners: {
      redemptionEmailRequired: {
        message: 'Verify and scan your email address in order to redeem your points. ',
        buttonText: 'Scan email address',
      },
      optBackIn: {
        message: 'You have opted out of all data sales. You are no longer eligible to earn from Reklaim. ',
        buttonText: 'Opt back in',
      },
    },
    notifications: {
      title: 'NOTIFICATIONS',
      emailAddress: {
        label: 'Email notifications',
        description: (primaryEmailAddress) =>
          `We'll email ${primaryEmailAddress || 'you'} with important updates regarding your paycheck, etc.`,
      },
      sms: {
        label: 'SMS notifications',
        description: (primaryPhoneNumber) =>
          `We'll text ${primaryPhoneNumber || 'you'} with important updates regarding your paycheck, etc.`,
      },
    },
    addNewEmailAddressModal: {
      emailAddressAddForm: {
        addTitle: 'SCAN NEW EMAIL ADDRESS',
        addText: 'In order to scan this email address for exposed data, you must verify it.',
        emailAddressPlaceholder: 'Enter your email address',
        invalidEmailAddressMessage: 'Please enter a valid email address',
        sameEmailMessage:
          'This email address is already linked to your account. Please enter a different email address',
        buttonText: 'Send Verification',
        sendVerificationEmailFailed:
          'This email address is already linked to another account. Please enter a different email address.',
      },
      emailAddressAddConfirmation: {
        title: "LET'S VERIFY YOUR EMAIL!",
        description: 'Please check the email we just sent to ',
        resendLabel: "Don't see the email?",
        resendButton: 'Resend',
        resendFailed: 'Could not resend the verification email. Please try again later.',
        emailSent: 'Sent! Please check your inbox',
        footerText: 'Already verified your email but still seeing this screen?',
        footerButtonText: "Let's Fix This",
        footerButtonTextChecked:
          'The new email is not yet verified. Please check your email for the verification link.',
      },
      emailAddressAddSuccess: {
        title: 'SUCCESSFULLY VERIFIED',
        message: 'Your identity has been confirmed, now we can see what data is out there on you.',
        button: 'Start scanning',
        continue: 'Continue',
      },
      emailAddressAddFailure: {
        title: 'VERIFICATION UNSUCCESSFUL',
        message: "Something went wrong and your email couldn't be verified. Try sending another verification email.",
        button: 'Resend verification email',
        resendFailed: 'Could not resend the verification email. Please try again later.',
      },
      errorMessages: {
        invalidEmailIdentifierError: 'The requested email address is invalid.',
        identifierEmailLimitError: 'Account has reached the limit of verified email addresses.',
        identifierEmailTakenError: 'The email address is taken.',
        cognitoPoolError: 'Please re-login to remove the requested identifier.',
        defaultError: 'Something went wrong. Please try again later.',
      },
    },
    securityAndPrivacy: {
      title: 'SECURITY AND PRIVACY',
      dataSales: {
        optOutLabel: 'Opt-out of data sales',
        optOutDescription: 'You will no longer be able to be compensated by Reklaim for your data.',
        optOutButton: 'Opt-out',
        optInButton: 'Opt-in',
        optOutModal: {
          title: 'ARE YOU SURE YOU WANT TO OPT OUT OF ALL DATA SALES?',
          message:
            'When you opt out of data sales, you will not be able to earn. This means you will no longer be able to earn some sweet, sweet money via Reklaim.',
          cancelButton: 'Cancel',
          confirmationButton: 'Opt-out of data sales',
        },
      },
      downloadData: {
        downloadLabel: 'Download my data',
        downloadDescription:
          'All the data we collect from you is fully available for you to view and download in JSON format.',
        downloadButton: 'Download',
        downloadModal: {
          title: 'DOWNLOAD MY DATA',
          description:
            'Would you like a copy of your encrypted data? Not a problem! Click below to download your data via a JSON file. By the way, Killi will never have access to this data. Like, never.',
          jsonTitle: 'WHAT THE HECK IS A JSON FILE?',
          jsonDescription:
            'A JSON file is a file that stores simple data structures and objects. It contains data in a standard data interchange format which is lightweight, text-based and human-readable.',
          jsonParserDescription: {
            part1: 'If you need a JSON reader to view the file, please visit ',
            part2: 'JSON Parser Online',
            part3: '.',
          },
          downloadButton: 'Download',
          cancelButton: 'Cancel',
        },
      },
      deleteAccount: {
        deleteAccountLabel: 'Delete my account',
        deleteAccountDescription: "Permanently delete your account. This can't be undone.",
        deleteAccountButton: 'Delete',
        deleteAccountModal: {
          title: 'ARE YOU SURE YOU WANT TO DELETE YOUR ACCOUNT?',
          description:
            'We are sorry to see you leave. By deleting your account ALL of your Reklaim data as well as any money you have in the Reklaim app will be removed and unrecoverable.',
          cancelButton: 'Cancel',
          deleteAccountButton: 'Yes, delete my account',
        },
      },
    },
    profilePictureSection: {
      modalTitle: 'UPDATE PROFILE PICTURE',
      saveButton: 'Save',
      cancelButton: 'Cancel',
      errorMessages: {
        fileSizeTooLarge: 'The image is too large in size. Please try again with smaller image.',
        uploadFailed: 'Something went wrong while uploading the image. Please try again later.',
      },
    },
  },
  reklaim: {
    hero: {
      header: 'Reklaim Pro',
      title: 'PROTECT YOUR DIGITAL PRIVACY',
      navigateToSubscriptionScreen: 'Upgrade your privacy',
    },
    tasksSection: {
      title: 'TASKS',
      profileTask: {
        title: 'PROFILE',
        requirement1: 'Share Your Profile',
        requirement2: 'Provide ZIP/Postal, Date of birth, and Gender',
      },
      shoppingTask: {
        title: 'SHOPPING',
        requirement1: 'Connect 1 bank account',
        requirement2: 'Make 1 transaction this week',
      },
      browsingTask: {
        title: 'BROWSING',
        requirement1: 'Opt-in to sharing browsing data',
        requirement2: 'Browse 1 URL this week with the extension installed',
      },
      deviceTask: {
        title: 'DEVICE',
        requirement1: 'Share your device info and which apps you have',
      },
    },
    footprintSection: {
      title: 'YOUR FOOTPRINT',
      fetchErrorMessage: 'Failed to load the data. Please revisit and try again later.',
    },
    referAFriendCard: {
      title: (referralAmount) => `RECEIVE ${referralAmount} POINTS PER FRIEND`,
      subText: 'Start referring',
    },
    smsNotificationModal: {
      addPhoneNumber: {
        addTitle: 'SCAN NEW PHONE NUMBER',
        description: 'In order to scan this phone number for exposed data, you must verify it.',
        button: 'Send verification',
      },
      enableNotification: {
        title: 'CONFIRM YOUR PHONE NUMBER',
        button: 'Enable notifications',
      },
      verificationCode: {
        title: 'VERIFY TO GET YOUR DATA',
        message: "We've sent a verification code to",
        resend: 'Resend verification code',
        button: 'Verify',
        codeVerificationResendSent: 'Sent! Please check your inbox',
      },
      phoneNumberSuccess: {
        title: 'SUCCESSFULLY VERIFIED',
        message: 'Your identity has been confirmed, now we can see what data is out there on you.',
        scanningText: 'Start scanning',
        continueText: 'Continue',
      },
      errorMessages: {
        invalidSmsCodeError: 'Invalid SMS code. Please try again later.',
        invalidPhoneIdentifierError: 'The requested phone number is invalid.',
        identifierPhoneLimitError: 'Account has reached the limit of verified phone number.',
        identifierPhoneTakenError: 'The phone number is taken.',
        cognitoPoolError: 'Please re-login to remove the requested identifier.',
        defaultError: 'Something went wrong. Please try again later.',
      },
      footnote:
        'By inputting your phone number, you agree to receive autodialed marketing text messages from Reklaim in connection with payout alerts at the telephone # used to opt-in. Message and data rates may apply. You will receive recurring messages each month. For more details read our ',
      termsAndConditions: 'Terms & Conditions',
      and: ' and ',
      privacyPolicy: 'Privacy Policy',
      dot: '.',
      // Phone Number Input
      invalidPhoneNumberMessage: 'Please enter a valid phone number',
      samePhoneNumberMessage:
        'This phone number is already linked to your account. Please enter a different phone number.',
      codeVerificationSendFailed: 'Could not send the verification code. Please try again later.',
      // Verification Code Input
      codeVerificationInvalid: 'The code provided is invalid. Please try again.',
      codeVerificationResendFailed: 'Could not send the verification code. Please try again later.',
      // Enable Notification
      enableSMSNotificationsFailed: 'Could not enable SMS notifications. Please try again later.',
      // Disable Notification
      disableSMSNotificationsFailed: 'Could not disable SMS notifications. Please try again later.',
      disableNotificationModal: {
        title: 'ARE YOU SURE YOU WANT TO DISABLE SMS NOTIFICATIONS?',
        description: 'You will no longer be notified when your earnings have been deposited in your account',
        cancelButton: 'I want to be notified',
        disableButton: "I don't want to be notified",
      },
    },
  },
  close: 'Close',
  skip: 'Skip',
  next: 'Next',
  back: 'Back',
  day: 'Day',
  month: 'Month',
  year: 'Year',
  dateOfBirthExplainer: {
    minDateMessage: (minDate) => `Date should not be before minimal date, ${minDate}`,
    maxDateMessage: (maxDate) => `Date should not be after maximal date, ${maxDate}`,
    invalidDateMessage: 'Invalid Date Format (mm-dd-yyyy)',
  },
  gender: {
    reason: 'Your gender allows us to qualify you for content that is relevant to you.',
    reasonTitle: 'Why do we ask for this info?',
    placeholder: 'Select Gender',
    male: 'Male',
    female: 'Female',
    nonBinary: 'Non-Binary',
    values: {
      [GENDER_VALUE.MALE]: 'Male',
      [GENDER_VALUE.FEMALE]: 'Female',
      [GENDER_VALUE.NON_BINARY]: 'Non-Binary',
    },
  },

  // error
  anErrorHasOccurred: 'An Error Has Occurred',
  unveilScreen: {
    summarySection: {
      titleUserHandle: (userHandle) => `${userHandle}'s DIGITAL FOOTPRINT SUMMARY`,
      titleNoUserHandle: 'MY DIGITAL FOOTPRINT SUMMARY',
    },
    attributeCount: ' attributes found',
    showMoreButton: 'Show more',
    breachesSection: {
      title: 'WHERE HAS MY DATA BEEN FOUND?',
      title1: 'EXPOSED',
      title2: ' IN DATA BREACHES',
      noBreachesTitle1: 'COULD HAVE BEEN ',
      noBreachesTitle2: 'EXPOSED',
      noBreachesTitle3: ' IN DATA BREACHES',
    },
    attributesSection: {
      title: 'WHAT DATA HAS BEEN FOUND?',
      noProfileAttributesTitle1: 'COULD HAVE BEEN ',
      noProfileAttributesTitle2: 'COLLECTED',
      noProfileAttributesTitle3: ' FROM YOU BY DATA BROKERS',
    },
    newUser: {
      innacurateDataWarning: 'If this data is inaccurate, you can edit it later in Reklaim',
      completeButton: 'Done',
      continueButton: 'Continue',
      successModal: {
        title: 'WELCOME TO REKLAIM!',
        noAssociatedIdsDescription: 'Your data profile has been created. We will now detect any of your leaked data.',
        withAssociatedIdsDescription:
          'Your data profile has been created and the below email addresses and phone numbers will be stored in your Account. In order for us to detect any leaked data associated with them, you must verify them.',
        continueButton: 'Finish',
      },
    },
    existingUser: {
      addButton: 'Add to My Data',
      skipButton: "Don't add",
      successModal: {
        title: 'SUCCESSFULLY ADDED TO YOUR DATA PROFILE',
        noAssociatedIdsDescription: 'Your data profile has been saved.',
        withAssociatedIdsDescription:
          'Your data profile has been saved and the below email addresses and phone numbers will be stored in your Account. In order for us to detect any leaked data associated with them, you must verify them.',
        continueButton: 'Finish',
      },
      unveilScanningModals: {
        unveilProspectingDataSecureModal: {
          title: "Your data is secure, let's help keep it that way",
          description1: "Your personal information wasn't detected in any data breaches.",
          description2:
            "However, 14+ new data breaches are reported each week. If your data (passwords, phone number, address etc.) is ever leaked, we'll be able to show you what was leaked and from where with one of our privacy plans.",
          continueButton: 'View plans',
          skipButton: 'No thanks',
        },
        unveilProspectingDataCompromisedModal: {
          title: 'Your data has been compromised',
          description:
            'Your personal information was detected in a data breach. Subscribe to one of our privacy plans to see your breached data.',
          continueButton: 'View plans',
          skipButton: 'No thanks',
        },
        unveilProspectingNoEmailModal: {
          title: 'We could not scan for your data',
          description:
            'Free users must have an email address added to their account in order for us to scan for their breached data.',
          continueButton: 'View plans',
          skipButton: 'Add email address',
        },
        unveilDataSecureModal: {
          title: 'Your data is secure',
          description: (reportedDataBreaches) =>
            `Your personal information wasn't detected in any of the ${reportedDataBreaches} reported data breaches since last scan.`,
          continueButton: 'Got it, thanks!',
        },
      },
    },
    conflictResolution: {
      questionDescription: 'Please verify which is the most up-to-date and accurate.',
      noAccurateOptionLabel: 'None of these are accurate',
      backButton: 'Back',
      nextButton: 'Next',
      doneButton: 'Done',
    },
    conflictResolutionModal: {
      title: 'WE DETECTED CONFLICTS IN YOUR DATA PROFILE',
      description:
        'Some of your data differs across the sources we scanned. Would you like to confirm which is the most up-to-date and accurate?',
      confirmButton: 'Yes',
      denyButton: 'No',
    },
  },
  breachCarousel: {
    passwordBreachType: {
      passwordTypeLabels: {
        exposed: 'Password leaked',
        hashed: 'Password encrypted',
        empty: 'Password not leaked',
      },
      passwordBreachModal: {
        title: 'PASSWORD BREACH STATUS',
        passwordLeaked: {
          description:
            'Your password has been exposed in plain text. Anyone can log into this account. You need to change your password immediately.',
        },
        passwordEncrypted: {
          description:
            "Your password has been exposed but in it's encrypted state. Someone would have to decrypt it in order to log into this account. You should change your password immediately.",
        },
        passwordNotLeaked: {
          description:
            "This password wasn't accessed during the breach. Although the personal information that was exposed in this breach could be used to figure out your password. You should change your password immediately.",
        },
        closeButtonLabel: 'Got it!',
      },
    },
    learnMoreButton: 'Learn more',
  },
  breachAttributesModal: {
    moreAttributesItem: (numAttributes) => `+${numAttributes} other attributes`,
    passwordLabel: 'Password',
    usersAffectedLabel: 'Users affected',
    dateLabel: 'Date',
    exposedDataTitle: 'EXPOSED DATA',
    continueButton: 'Got it!',
  },
  unveil: {
    promptModal: {
      goToUnveilButton: 'Unlock my data',
      cancelButton: "I don't care that my data is exposed",
      acknowledgeText:
        'I acknowledge that this is a complimentary service because Reklaim cares about consumer privacy.',
      title1: 'YOUR DATA COULD BE EXPOSED ON THE',
      title2: ' WEB',
      enterPhoneNumberPlaceholder: 'Phone number',
      invalidPhoneNumberMessage: 'Please enter a valid phone number',
      sendVerificationCodeFailed: 'Something went wrong. Try again later',
      phoneVerification: {
        verifySuccessTitle: 'SUCCESSFULLY VERIFIED',
        verifySuccessDescription: 'Your identity has been confirmed, now we can see what data is out there on you.',
        verifySuccessButton: 'Start scanning',
        verifyCodeTitle: 'VERIFY TO GET YOUR DATA',
        verifyCodeMessage: "We've sent a verification code to ",
        verifyCodeResend: 'Resend verification code',
        verifyCodeButton: 'Verify',
        verifyCodeVerificationResendSent: 'Sent! Please check your inbox',
        defaultErrorMessage: 'The code provided is incorrect. Please try again.',
      },
    },
    summaryModal: {
      secure: {
        title: 'YOUR DATA IS SECURE',
        description: (totalDataBreachCount) =>
          `Your personal information wasn't detected in any of the ${totalDataBreachCount} reported data breaches since last scan.`,
        buttonText: 'Got it, thanks!',
      },
      compromised: {
        title: 'YOUR DATA HAS BEEN COMPROMISED',
        description: (totalDataBreachCount, relatedDataBreachCount) =>
          `There were ${totalDataBreachCount} reported data breaches since last scan, your personal information was detected in ${relatedDataBreachCount} of them. Want to see where your information was compromised?`,
        showMeButtonText: 'Show me',
        noThanksButtonText: 'No thanks',
      },
    },
  },
  signUpWelcome: 'Your data. Your reward.',
  history: {
    pageHeader: 'History',
    pageSubHeader: 'Keeping track of your points is simple. All your earnings live right here!',
    transactionTableHeader: {
      date: 'DATE',
      action: 'ACTION',
      points: 'POINTS',
    },
    noTransactionHeader: 'Nothing to see here...yet!',
    noTransactionSubHeader: 'Your history will appear here once you start earning.',
    killiPaycheckTiersLabel: {
      beginner: 'Killi Paycheck - Beginner',
      intermediate: 'Killi Paycheck - Intermediate',
      pro: 'Killi Paycheck - Pro',
      elite: 'Killi Paycheck - Elite',
      allStar: 'Killi Paycheck - All Star',
    },
    referralPaycheckLabel: 'Refer a Friend Paycheck',
    crypto: {
      toReceive: 'to receive',
      walletAddressLabel: 'Receiving wallet address',
    },
  },
  jackpot: {
    currentPrize: 'Current Prize: ',
    multiPrize: (numPrizes) => `${numPrizes} Prizes Each Worth `,
    instructions: {
      title: 'How it works:',
      step1: 'Use the slider to set your points.',
      step2: 'More points = better odds!',
      step3: (numPrizes) =>
        `${numPrizes > 1 ? 'Jackpot prizes go to the lucky winners!' : 'One lucky winner takes the jackpot!'}`,
    },
    setYourPoints: 'Set Your Points to Enter:',
    autoPledge: 'Automatically pledge this amount each week',
    enterNow: 'Enter Now',
    cancelEntry: 'Cancel Entry',
    requestComplete: (action) => `Your entry has been ${action}`,
    requestPledgeErrorMessage: 'There was an issue creating your entry. Please try again later.',
    deletePledgeErrorMessage: 'There was an issue cancelling your entry. Please try again later.',
    getDataErrorMessage:
      'There was an issue processing the jackpot information. Please refresh the page or try again later.',
  },
  protect: {
    pageTitle: {
      part1: 'PROTECT YOUR ',
      part2: 'DATA',
    },
    subscriptionStatus: {
      title: 'REKLAIM PROTECT SUBSCRIPTION STATUS: ',
      subscribed: 'SUBSCRIBED',
      subscriptionExpired: 'SUBSCRIPTION EXPIRED',
      trial: 'TRIAL - UPGRADE TO ACCELERATE',
      trialExpired: 'TRIAL EXPIRED',
      undetermined: 'UNDETERMINED',
      subscribeLink: 'Subscribe',
      daysRemaining: {
        endedOn: 'Ended on: ',
        endsIn: 'Ends in: ',
        renewsIn: 'Renews in: ',
        unknown: 'Unknown',
      },
    },
    profileInfo: {
      title: 'USER PROFILE',
      titleComplete: ' - COMPLETE',
      titleInfoMissing: ' - INFO MISSING',
      description:
        'We use your profile to scan for data breaches and remove you from data brokers. Please ensure it is accurate!',
      descriptionError:
        'We ran into an issue retrieving your profile information. Please refresh the page to try again.',
      nameLabel: 'Name: ',
      emailLabel: 'Email: ',
      addressLabel: 'Address: ',
      birthDateLabel: 'Date of Birth: ',
      edit: {
        title: 'EDIT YOUR PROFILE',
        emailAddresses: {
          title: 'EMAIL ADDRESSES',
          primaryPill: 'Primary',
          options: {
            makePrimary: 'Make Primary',
            add: 'Add',
            delete: 'Delete',
          },
        },
        addEmailAddress: 'Add Email Address',
        save: 'Save',
        dataBrokerRemovalContract: {
          signed: 'Signed',
          unsigned: 'Requires Signature',
          title: 'CONTRACT',
          viewButton: 'View Contract',
          voidButton: 'Void Contract',
          signButton: 'Review & Sign',
          voidSuccess: 'Your contract has been voided successfully.',
          voidError: 'We were not able to void your contract. Please try again later.',
          signSuccess: 'Success! Contract has been signed',
          signError: 'There was an error creating your contract. Please try again later.',
          missingUserProfileError: 'Please complete your profile before signing the contract.',
        },
        error: {
          noData: 'We could not find your profile information. Please refresh the page to try again.',
          requiredField: 'This field is required.',
          emailInvalid: 'Please enter a valid email address.',
          zipInvalid: (type) => `Please enter a valid ${type}.`,
          emptyFields: 'Please fill in all fields before saving.',
          updateFailed: 'We were not able to update your profile information. Please try again later.',
        },
      },
    },
    deleteStatus: {
      title: 'DATA BROKER REMOVAL IN PROGRESS:',
    },
    breachStatus: {
      titleBreaches: (amount) => `${amount} DATA BREACHES `,
      titleFound: 'FOUND',
      description: (amount) =>
        `We've identified the following data breach${
          amount > 1 ? 'es' : ''
        } linked to your email address. We understand this might be concerning, and we want to assure you that your security is our top priority.`,
      descriptionAllClear: "We've scanned your email addresses and found no data breaches!",
      descriptionError:
        'We ran into an issue scanning your account for data breaches. Please refresh the page to try again.',
      seeMore: 'See More',
    },
    chatbotCTA:
      'Chat with your personal privacy assistant for any other questions about breaches, data brokers, and all things data privacy now: ',
    chatbotLink: 'https://ai.reklaimyours.com',
  },
  redemptionStore: {
    pageTitle: {
      part1: 'CHOOSE YOUR ',
      part2: 'REWARD',
    },
    shares: {
      categoryName: 'Shares in Reklaim',
    },
  },
  wallet: {
    pageTitle: {
      part1: 'REDEEM YOUR ',
      part2: 'POINTS',
    },
    rewards: {
      description: 'Description',
      redemptionRange: (minRedemptionAmount, maxRedemptionAmount) => `${minRedemptionAmount} - ${maxRedemptionAmount}`,
      redemptionSingle: (minRedemptionAmount) => `${minRedemptionAmount}+ points`,
      products: {
        unavailable: (idType) => `Please return to the app and add a primary ${idType} to your account to redeem.`,
        namedUnavailable: (idType, productName) =>
          `Please return to the app and add a primary ${idType} to your account so you can redeem to ${productName}.`,
      },
    },
    estimatedPayoutText: 'PAID IN ',
    monthlyEarningsText: 'Monthly earnings —',
    monthlyEarningsAmount: (monthlyEarnings) => `${monthlyEarnings} ${monthlyEarnings > 1 ? 'PTS' : 'PT'}.`,
    remainingDays: (remainingDays) => `${remainingDays} ${remainingDays > 1 ? 'DAYS' : 'DAY'}`,
    paycheckToday: 'TODAY!',
    estimatedPaycheck: (estimatedAmount) => `YOU'LL EARN ${estimatedAmount} THIS WEEK`,
    redeemTitle: 'REDEEM YOUR ',
    redeemTitleAccent: 'POINTS',
    transactionTableHeader: {
      date: 'Date',
      description: 'Description',
      points: 'Points',
    },
    noGoal: {
      title: 'No goal set',
      description: 'Set a goal to redeem your points',
    },
    notEligible: {
      title: 'Not eligible',
      description: 'You must be eligible to redeem your points',
    },
    openRedemption: {
      title: 'SUCCESS',
      pending: 'Your redemption is now pending approval.',
      processing: 'Your redemption is being processed.',
    },
    approvedRedemption: (remainingDays) =>
      `Your redemption has been approved! You can redeem again in ${remainingDays} days.`,
    noTransactionHeader: 'Nothing to see here...yet!',
    noTransactionSubHeader: 'Your funds will appear here once you start earning.',
    myDataEarnings: 'My Data earnings',
    redeemButton: 'Redeem points',
    referralPaycheckLabel: 'Refer a Friend Paycheck',
    crypto: {
      toReceive: 'to receive',
      walletAddressLabel: 'Receiving wallet address',
    },
    redemptions: {
      redemptionRequirements: {
        requirementHeader: 'BEFORE YOU CAN REDEEM',
        promptUserRefresh: 'There was an error fetching the redemption requirements. Please refresh the page.',
        accountMaturity: (minimumAccountAge) => `Your account must be at least ${minimumAccountAge} days old.`,
        accountMaturityRemainingDays: (remainingDays) => ` You have ${remainingDays} days to go.`,
        onlyOneOpenRedemption: 'You can only have one open redemption request at a time.',
        noRecentRedemption: (minRedemptionInterval) =>
          `You've made no redemptions in the last ${minRedemptionInterval} days.`,
        noRecentRedemptionRemainingDays: (remainingDays) => ` You have ${remainingDays} days to go.`,
        requirementMultipleAccounts: {
          label: 'No other account is using your device. ',
          fixButton: 'Fix this',
        },
        requirementEmailVerification: 'You must have a verified email on your account. ',
        requirementPhoneVerification: 'You must have a verified phone number on your account. ',
        missingIdentifierFix: 'Please add one in the Reklaim app.',
        gotItButton: 'Got it',
      },
      redemptionBanner: {
        bannerDescription: 'You do not meet the requirements in order to redeem your points. ',
        bannerLinkText: 'Learn more',
      },
      redemptionVerifyEmail: 'Verify email',
      redemptionSelectionTitle: 'CHOOSE YOUR REDEMPTION METHOD',
      redemptionVerificationWarning:
        'Your account has not been verified by Reklaim yet. You can still request redemption but there may be up to a 5 day delay as we do a one-time review of your account.',
      giftCardHero: {
        headerTitle: 'REDEEM GIFTCARD OR DONATION',
        italicHeader: 'Your balance —',
      },
      redemptionOptionsHelp: 'Available redemption options',
      recaptchaErrorMessage: "We're having some trouble validating your request. Please contact support.",
      recaptchaErrorSupport: (screenName) => `I'm having trouble validating a request on the ${screenName} screen.`,
      cashRedemption: {
        pointsSuffix: ' PTS',
        pointsPlaceholder: '1,000 PTS',
        minPointsAmountErrorMessage: (minAmount) => `Min amount to redeem is ${minAmount}`,
        pointsGreaterThanBalance: 'Amount entered cannot be greater than balance',
        pointsGreaterThanMaxRedemptionValue: 'Amount entered cannot be greater than valid redemption amount.',
        dollarAmount: '$ amount',
        redemptionFee: 'Redemption fee',
        dollarToReceive: '$ to receive',
        accountSelector: {
          paypal: {
            title: 'Sending to PayPal Account:',
            placeholder: 'Select your PayPal email address:',
          },
          venmo: {
            title: 'Sending to Venmo Account',
            placeholder: 'Select your Venmo phone number:',
          },
        },
        recipientNote: {
          paypal: 'NOTE: Your PayPal account must be registered under your Reklaim email address above',
          venmo: 'NOTE: Your Venmo account must be registered under your Reklaim phone number above',
        },
        cashRedeemPromptDescription: (formattedAmount, accountType) =>
          `Are you sure you want to redeem ${formattedAmount} to your ${accountType} account? Once submitted, this cannot be cancelled!`,
        continueButton: 'Continue',
      },
      giftCardRedemption: {
        redeemSelectionTitle: 'REDEMPTION AMOUNT',
        redeemSelectionButton: 'Continue',
        redeemCancelButton: 'Choose another reward',
        pointsToDollar: (points, dollar) => `${points} = ${dollar}`,
        accountSelector: {
          title: 'Sending gift card to:',
          placeholder: 'Select email to redeem to:',
        },
        redeemProcessNote: 'Please note it takes 3-5 business days for your redemption request to be processed.',
        redeemSuccessTitle: "WE'VE GOT YOUR REDEMPTION REQUEST!",
        redeemSuccessDescription1:
          "Your request is being processed and may take 3-5 business days to complete. Once processed, you'll receive a confirmation email.",
        redeemSuccessDescription2: 'If you have any questions, contact support at ',
        redeemSuccessDescription3: '!',
        redeemPromptTitle: 'ARE YOU SURE?',
        redeemPromptDescription: (formattedAmount) =>
          `Are you sure you want to redeem your ${formattedAmount} gift card? Once submitted, this cannot be cancelled!`,
        redeemPromptConfirm: 'Yes, redeem',
        redeemPromptCancel: "No, don't redeem",
        redeemFailureMessage:
          'We could not process your redemption request. Please try changing the redemption amount or try again later.',
        redeemSuccessConfirm: 'Done',
      },
      shares: {
        missingIdModal: {
          title: 'MISSING INFO',
          error: 'You must have a verified email address and phone number on your account to redeem Reklaim shares.',
          verify: 'Verify',
        },
        missingIdError: 'A verified email address and phone number are required to proceed.',
        lowfunds: 'You do not have enough points to redeem Reklaim shares.',
        numberOfShares: (numShares) => `${numShares} share${numShares === '1' ? '' : 's'}`,
        sharePrice: 'Share Price:',
        ptsAmount: 'PTS Amount:',
        requirements: 'You will need to add/update the following info in the next step:',
        redeemFailureMessage:
          'We could not process your redemption request. Please try changing the redemption amount or try again later.',
        continue: 'Continue',
        chooseAnotherReward: 'Choose another reward',
        submitted: {
          title: "WE'VE GOT YOUR REDEMPTION REQUEST!",
          description1:
            "Your request is being processed and may take 3-5 business days to complete. Once processed, you'll receive a confirmation email.",
          description2: 'If you have any questions, contact support at ',
          description3: '!',
          confirm: 'Done',
        },
        closed: {
          title: 'REDEMPTION IN REKLAIM SHARES IS CURRENTLY CLOSED!',
          disabled1: 'Redemption is currently unavailable. ',
          disabled2: "To get notified when it reopens, check the box and we'll send you an update!",
          autoRedeem1: 'Prefer to skip the manual step? ',
          autoRedeem2: 'Enroll in our re-investment plan to redeem my balance for shares whenever available.',
        },
        autoRedeem:
          "I want to enroll in Reklaim's re-investment plan to redeem my balance for shares whenever available.",
      },
      socialShare: {
        title: "YOU'VE REDEEMED YOUR POINTS SUCCESSFULLY",
        shareText: 'SHARE YOUR REDEMPTION EXPERIENCE WITH YOUR FRIENDS!',
        copyLink: 'Copy link',
        copiedLink: 'Link copied!',
        shareLink: 'Share my link',
        returnToApp: 'Return to app',
      },
    },
  },
  emailVerification: {
    requirementEmailVerification: 'You must have a verified email in order to redeem your points.',
    verifyEmailText: 'Verify Email.',
  },
  crypto: {
    [CRYPTO_KEYS.BITCOIN]: {
      label: 'Bitcoin',
      ticker: 'BTC',
      transactionLinkLabel: 'Blockchain',
    },
    [CRYPTO_KEYS.ETHEREUM]: {
      label: 'Ethereum',
      ticker: 'ETH',
      transactionLinkLabel: 'Etherscan',
    },
    [CRYPTO_KEYS.USDC]: {
      label: 'USDC',
      ticker: 'USDC',
      transactionLinkLabel: 'Polygon',
    },
  },
  redemptionOptions: {
    screenTitle: 'I would like to redeem',
    giftCardTitle: 'Giftcard or Donation',
    giftCardDescription: 'Amazon, Visa, and many more!',
    disabledOptionMessage: (redemptionName) => `${redemptionName} redemption is currently disabled`,
    remainingPointsToRedeem: (amount) => `You need ${amount} more points`,
  },
  cryptoRedeemScreen: {
    rateExpiresIn: 'RATE EXPIRES ',
    slashBar: '/',
    pointsConversionScreen: {
      screenTitle: (cryptoName) => `REDEEMING ${cryptoName}`,
      ptsToRedeem: 'PTS to redeem',
      pointsSuffix: ' PTS',
      pointsPlaceholder: '1000 PTS',
      minPointsAmountErrorMessage: (minAmount) => `Min amount to redeem in crypto is ${minAmount}`,
      pointsGreaterThanBalance: 'Amount entered cannot be greater than balance',
      cryptoToReceive: (cryptoTicker) => `${cryptoTicker} to receive`,
      approximateText: (approximateAmount, currency) => ` ≈ ${approximateAmount} ${currency}`,
      continueButton: 'Continue',
    },
    walletEnteringScreen: {
      screenTitle: 'YOUR WALLET ADDRESS',
      polygonWarning:
        'For the lowest fees, we transfer USDC using the Polygon network. Please enter a Polygon wallet address below. ',
      polygonLearnMore: 'Learn More',
      formPlaceholder: 'Enter your wallet address',
      pasteAddressButtonText: 'Paste address',
      walletAddressErrorMessage: (crypto) => `Please enter a valid wallet address for ${crypto}`,
      continueButton: 'Continue',
      incorrectAddressWarning:
        'Do not enter the address by hand. Entering the incorrect address will result in transfers to the wrong account and cannot be undone.',
    },
    confirmationScreen: {
      screenTitle: 'CONFIRM REDEMPTION',
      receivingWalletAddress: 'Receiving wallet address',
      ptsToRedeem: 'PTS to redeem',
      cryptoToReceive: (cryptoTicker) => `${cryptoTicker} to receive`,
      amount: (cryptoTicker) => `${cryptoTicker} amount`,
      networkFee: 'Estimated Network Fee',
      isFeeHigherThanAmountToReceive:
        "Redemption can't be requested. Estimated network fee is higher than amount to redeem.",
      infoModal: {
        title: "WHAT'S A NETWORK FEE?",
        description:
          'A network fee is required to ensure your transaction is confirmed by the mining network and fluctuates based on volume on the network. Reklaim does not profit from it.',
        buttonText: 'Got it!',
      },
      buttonText: 'Request',
      redemptionRequestFailed:
        'We could not process your redemption request. Please try again later or contact support.',
    },
    cryptoRedeemSuccessModal: {
      title: "WOO, WE'VE GOT YOUR REDEMPTION REQUEST!",
      requestToSend: 'Request to send',
      toWalletAddress: 'To Wallet Address',
      cryptoRedeemDescription1:
        "Your request is being processed and may take 3-5 business days to complete. Once processed, you'll receive a confirmation email.",
      cryptoRedeemDescription2: 'Any questions, give us a shout at ',
      cryptoRedeemDescription3: '!',
      redeemSuccessConfirm: 'Done',
    },
    rateExpiredModal: {
      title: 'YOUR RATE HAS EXPIRED!',
      description: (quoteTimeout) =>
        `Due to crypto's rapidly changing rates, there is a ${quoteTimeout} minute timer on the rate we give you.`,
      buttonText: 'Restart',
    },
  },

  lockedScreen: {
    title: 'YOUR ACCOUNT HAS BEEN SUSPENDED',
    description: 'A large amount of suspicious activity was detected on this account and it has been suspended.',
    button: 'I understand',
  },
  networkError: {
    supportInitialMessage: (region, ipAddress) =>
      `Region: ${region || 'N/A'}\nIP Address: ${ipAddress || 'N/A'}\nMessage:\n`,
    regionNotSupported: {
      title: 'WELL THIS IS AWKWARD!',
      description1: "You're in a region we don't support yet.",
      description2:
        'If you are in a supported region but still see this error message, let our customer support know at ',
      button: 'See Supported Regions',
      contactSupportButton: 'Contact support',
      detectedRegion: 'Detected Region: ',
      ipAddress: 'IP Address: ',
      notApplicable: 'N/A',
    },
    noInternetConnection: {
      title: 'NO INTERNET CONNECTION',
      description: 'You seem to be experiencing network issues. Please check your internet connection and try again.',
      bannerText: 'Network error. Check your internet connection.',
    },
  },
  updateEmailUnauthenticated: {
    successModal: {
      title: 'NOT A ROBOT IN SIGHT! YOUR EMAIL IS NOW VERIFIED.',
      message: 'Please use your new email to log in from now on.',
      button: 'Continue',
    },
    failureModal: {
      title: 'VERIFICATION UNSUCCESSFUL',
      message:
        "Something went wrong and your email couldn't be verified. If you've already verified your email with us, you can log into your account.",
      button: 'Go to log in',
    },
  },
  referAFriend: {
    earningCard: {
      cardTitle: 'EARNINGS',
      totalEarned: 'Total earned',
      nextWeeksPayout: "Next week's payout",
    },
    shareCard: {
      title: 'FOR EACH FRIEND YOU BRING TO REKLAIM, RECEIVE ',
      shareTitle: (bonusAmount, referralRewardsPercentage) =>
        `${bonusAmount} signup bonus + ${referralRewardsPercentage}% Killi Paycheck earnings of all friends you refer`,
      maxReferralLimit: (referrerBonusLimit) => ` /${referrerBonusLimit}`,
      copyButton: 'Copy',
      copiedButton: 'Copied',
    },
    descriptionCard: {
      cardTitle: 'HOW IT WORKS',
      itemSendInvite: 'Send your invite code to your friends.',
      itemSendInviteLimited: (referrerBonusLimit) =>
        `Send your invite code to your friends to up to ${referrerBonusLimit} friends`,
      itemMonetization: 'Your friend must sign up and join Profile Rewards.',
      itemRewardsEarnings: (bonusAmount, referralRewardsPercentage) =>
        `You'll both get ${bonusAmount}, and you'll get ${referralRewardsPercentage}% of your friends weekly data earnings.`,
    },
  },
  joinAFriend: {
    title: 'Welcome to Killi',
    description:
      'Hey there! Friends know you best which is why your friend invited you to join Killi! Killi gives you control over your data and allows you to get paid for it as well! This calls for a celebration!',
    subtitle: 'Ready to join the Data Revolution?',
    signupButton: 'Sign Up',
    bonusSectionTitle: 'How to get your bonus',
    bonusStep1: 'Sign up',
    bonusStep2: 'Get your bonus!',
  },
  pollfishSurveyScreen: {
    noIframeMessage: 'Your browser does not support iframe. Please use a browser which supports iframe.',
  },
  killiRewards: {
    title: 'Killi Paycheck',
    earnMore: 'Earn more from your data',
    description: ['Earn based on the amount of data you share. The more data you share, the more you can earn!'],
    paycheckTiers: 'Paycheck Tiers',
    beginnerLevelTitle: 'Beginner',
    intermediateLevelTitle: 'Intermediate',
    proLevelTitle: 'Pro',
    eliteLevelTitle: 'Elite',
    allStarLevelTitle: 'All-star',
    intermediateLevelDescription: 'Enroll in ONE Killi Reward.',
    proLevelDescription: 'Enroll in TWO Killi Rewards.',
    eliteLevelDescription: 'Enroll in THREE Killi Rewards.',
    allStarLevelDescription: 'Enroll in FOUR Killi Rewards.',
    intermediatePaycheckAmount: '100',
    proPaycheckAmount: '200',
    elitePaycheckAmount: '300',
    allStarPaycheckAmount: '400',
    perMonth: '/mo',
    payoutTimingInformation: (paycheckDayOfWeek) => `Paycheck is paid out on ${paycheckDayOfWeek} at 5pm Eastern Time.`,
    questionMarkAlt: 'question mark',
    currentTier: 'Current tier:',
    newTierLabel: (rewardName) => `After joining ${rewardName}:`,
  },
  myData: {
    hero: {
      header: {
        part1: "It's yours,",
        part2: 'take it back —',
      },
      title: 'YOUR DATA COULD BE EXPOSED',
      navigateToAccountScreen: 'Scan more accounts',
      earnMore: 'Earn more from my data',
    },
    tabs: {
      profile: 'Profile',
      browsing: 'Browsing',
      shopping: 'Shopping',
      device: 'Device',
    },
  },
  dividends: {
    sectionTitle: 'Earn Rewards',
    sectionDescription: 'Earn while you sleep! Share your info and earn even more!',
    arrowIconAlt: 'Arrow Icon',
    inProgressIndicator: 'Not earning',
    eligibleIndicator: 'Earning',
    currentEarningsDescription: "This week's earnings",
    enrollmentTotalEarningsDescription: 'Total Earnings',
    statuses: {
      notMember: 'Start earning',
      inProgress: 'Complete tasks',
      learnMore: 'Learn more',
    },
    tasksHeader: 'Tasks',
    tasksEventsDelayNotice: 'Some tasks may take up to 24 hours to update',
    profile: {
      cardTitle: 'Profile Rewards',
      cardDescription: 'Be part of the community and earn from your data.',
      requiredAttributesNote: 'Indicates missing info',
      shareProfileButton: 'Share Profile',
      completeProfileButton: 'Complete Profile',
      optOutButton: 'Stop sharing my Profile data',
      consentDate: (date) => `Consent Date: ${date}`,
      miniIntakeModal: {
        nextButton: 'Next',
        shareButton: 'Share Profile',
      },
      optOutModal: {
        title: 'ARE YOU SURE YOU WANT TO STOP SHARING YOUR DATA?',
        message: 'Unsharing this data will make you ineligible to earn from it.',
        cancelButton: 'Continue sharing this data',
        optOutButton: 'Unshare this data',
      },
      sharedData: {
        sectionTitle: 'This is the data you share',
        generalTitle: 'General',
        demographicTitle: 'Demographic',
        employmentTitle: 'Employment',
        financialTitle: 'Financial',
        householdTitle: 'Household',
        interestsTitle: 'Interests',
        generalDateOfBirthTitle: 'Date of Birth',
        generalGenderTitle: 'Gender',
        generalPostalCodeTitle: 'Postal Code',
        generalCountryTitle: 'Country',
        infoSource: (source) => `Source: ${source}`,
      },
    },
    referAFriend: {
      cardTitle: 'Refer',
      cardDescription: (bonusAmount, referralRewardsPercentage) =>
        `${bonusAmount} signup bonus + ${referralRewardsPercentage}% Killi Paycheck earnings of all friends you refer`,
      cardCTA: 'Start referring',
    },
    browserExtension: {
      warningBannerText:
        'Browser Rewards is changing to give you cashback for hundreds of online merchants - the more you shop the more you earn. Upgrade below!',
      extensionBrowserRewards: {
        earnFromTitle: 'EARN FROM ONLINE SHOPPING',
        earnFromDescription: 'Download our Chrome extension to earn cashback while shopping online.',
        downloadExtensionButton: 'Download extension',
        learnMoreLinkText: 'Learn more',
      },
      eventsStatistics: {
        sectionTitle: 'BROWSING HISTORY',
        sectionTableHeader: 'VISITS PAST 6 MONTHS',
        otherCategory: 'Other',
        noEventsFoundTitle: 'No browsing history found',
        noEventsFoundDescription: 'Make sure you have the extension installed and check again in 24 hours',
      },
      optOutModal: {
        title: 'ARE YOU SURE YOU WANT TO STOP SHARING YOUR DATA?',
        message: 'Unsharing this data will make you ineligible to earn from it',
        cancelButton: 'Continue sharing this data',
        optOutButton: 'Unshare this data',
      },
      optOutButton: 'Stop sharing my browsing data',
    },
    shopping: {
      cardTitle: 'Shopping Rewards',
      cardDescription: 'Link your credit/debit card and earn a reward - nothing else to it!',
      disabledWarningMessage:
        "We're working on updates to Reklaim Rewards and for now this Reward is not accepting new members. Look for some exciting updates coming soon!",
      joinCard: {
        title: 'EARN FROM YOUR SHOPPING DATA',
        description: 'Connect your most active checking or credit card account.',
        button: 'Link card',
      },
      fastLink: {
        closeModal: {
          title: "ARE YOU SURE YOU DON'T WANT TO EARN?",
          message: "If you exit before linking your account, you won't be eligible to earn from your shopping data.",
          cancelButton: 'Continue',
          closeButton: 'Exit',
        },
        failureModal: {
          title: "WHOOPS, WE CAN'T CONNECT!",
          message: "The connection to your bank account has failed, but don't worry you can try again!",
          tryAgainButton: 'Try Again',
          cancelButton: 'Cancel',
        },
        successModal: {
          title: "CONGRATS, YOU'RE ALL SET UP!",
          message: 'Make 1 transaction per week on this card to earn from your shopping data.',
          closeButton: 'Got it!',
        },
      },
      eventsStatistics: {
        sectionTitle: 'SHOPPING HISTORY',
        sectionSpentHeader: 'SPENT PAST 6 MONTHS',
        otherCategory: 'Other',
        noEventsFoundTitle: 'No transaction history found',
        noEventsFoundDescription: 'Make sure the correct card is connected and check again in 24 hours',
        transactions: (numOfTransactions) =>
          `${numOfTransactions} ${numOfTransactions > 1 ? 'transactions' : 'transaction'}`,
      },
      accountsSection: {
        title: 'MY ACCOUNT',
        connectingMessage: "We're connecting your account. Please check again later.",
        deleteButton: 'Remove',
        deleteAccountModal: {
          title: "ARE YOU SURE YOU DON'T WANT TO EARN?",
          message: "Removing this account means you won't be eligible to earn from your shopping data.",
          errorMessage: 'Something went wrong. Please try again later or contact us',
          cancelButton: 'Keep account',
          deleteButton: 'Remove account',
        },
        noAccount: "You removed all of your accounts from shopping data and aren't earning anymore. ",
        startEarning: 'Start earning',
      },
      optOut: {
        buttonText: 'Stop sharing my shopping data',
        confirmModal: {
          title: "ARE YOU SURE YOU DON'T WANT TO EARN?",
          message: 'Unsharing this data will make you ineligible to earn from it. You can share again at any time.',
          cancelButton: 'Keep sharing',
          stopSharingButton: 'Stop sharing',
        },
      },
      optBackIn: {
        buttonText: 'Opt Back In',
        successModal: {
          title: 'WELCOME BACK',
          message: "You're now earning from your shopping data again.",
          closeButton: 'Thanks',
        },
      },
    },
    device: {
      cardTitle: 'Device Rewards',
      cardDescription: 'Share your device info and the names of your apps for a weekly payout!',
      screenHeader: 'Device info you share',
      optOutButton: 'Stop sharing my device data',
      signUpCard: {
        title: 'Device Rewards',
        description: 'Share your device info and the names of your apps for a weekly payout!',
        buttonText: 'Download the app',
      },
      tasks: {
        task1: 'Share your device info',
        task2: 'Share which apps you have',
        task3: 'Earn a weekly payout',
      },
      eligibilityTasks: {
        task1: 'Share your device info and which apps you have',
      },
      optedOut: {
        title: 'EARN FROM YOUR DEVICE DATA',
        message: 'On your mobile device, share your device info and the names of the apps you have to earn.',
      },
      deviceInfoSection: {
        titleSharing: 'DEVICE INFORMATION',
        fallbackValue: 'N/A',
        labels: {
          maid: 'MAID',
          deviceModel: 'Device Model',
          deviceOS: 'Device OS',
          serviceProvider: 'Service Provider',
          ipAddress: 'IP Address',
        },
      },
      installedAppsSection: {
        optedInTitle: 'INSTALLED APPS',
        notice: 'We do not get any data from the app, only the name of the app',
        emptyListTitle: 'No apps found',
        emptyListBody: 'We were not able to find any apps',
        appNames: {
          [APP_KEYS.AMAZON]: 'Amazon',
          [APP_KEYS.APPLE_MUSIC]: 'Apple Music',
          [APP_KEYS.DROPBOX]: 'Dropbox',
          [APP_KEYS.FACEBOOK]: 'Facebook',
          [APP_KEYS.FACEBOOK_MESSENGER]: 'Facebook Messenger',
          [APP_KEYS.GOOGLE_CHROME]: 'Google Chrome',
          [APP_KEYS.GOOGLE_DRIVE]: 'Google Drive',
          [APP_KEYS.GOOGLE_GMAIL]: 'Google Gmail',
          [APP_KEYS.GOOGLE_MAPS]: 'Google Maps',
          [APP_KEYS.GOOGLE_TRANSLATE]: 'Google Translate',
          [APP_KEYS.INSTAGRAM]: 'Instagram',
          [APP_KEYS.LINE]: 'LINE',
          [APP_KEYS.LINKED_IN]: 'LinkedIn',
          [APP_KEYS.LYFT]: 'Lyft',
          [APP_KEYS.NETFLIX]: 'Netflix',
          [APP_KEYS.PAY_PAL]: 'PayPal',
          [APP_KEYS.REDDIT]: 'Reddit',
          [APP_KEYS.SAFARI]: 'Safari',
          [APP_KEYS.SKYPE]: 'Skype',
          [APP_KEYS.SLACK]: 'Slack',
          [APP_KEYS.SNAPCHAT]: 'Snapchat',
          [APP_KEYS.SPOTIFY]: 'Spotify',
          [APP_KEYS.TIK_TOK]: 'TikTok',
          [APP_KEYS.TWITTER]: 'Twitter',
          [APP_KEYS.UBER]: 'Uber',
          [APP_KEYS.WAZE]: 'Waze',
          [APP_KEYS.WE_CHAT]: 'WeChat',
          [APP_KEYS.WHATS_APP]: 'WhatsApp',
          [APP_KEYS.YELP]: 'Yelp',
          [APP_KEYS.YOU_TUBE]: 'YouTube',
          [APP_KEYS.ZOOM]: 'Zoom',
        },
      },
      optOutModal: {
        title: 'ARE YOU SURE YOU WANT TO STOP SHARING YOUR DATA?',
        message: 'Unsharing this data will make you ineligible to earn from it',
        cancelButton: 'Continue sharing this data',
        optOutButton: 'Unshare this data',
        consentDate: (date) => `Consent Date: ${date}`,
      },
    },
  },
  profileRewardsQuestions: {
    profileInfoQuestions: {
      postalCode: {
        CA: 'Postal Code',
        US: 'Zip Code',
        SG: 'Postal Code',
        NZ: 'Postcode',
        AU: 'Postcode',
      },
      dateOfBirth: 'Date of Birth',
      gender: 'Gender',
    },
    categoryLabels: {
      [PROFILE_REWARDS_CATEGORIES.TRAIT]: 'Traits',
      [PROFILE_REWARDS_CATEGORIES.INTEREST]: 'Interests',
      [PROFILE_REWARDS_CATEGORIES.READING]: 'Reading preferences',
      [PROFILE_REWARDS_CATEGORIES.WATCHING]: 'Watching preferences',
      [PROFILE_REWARDS_CATEGORIES.HOUSEHOLD_INFORMATION]: 'Household information',
      [PROFILE_REWARDS_CATEGORIES.FINANCIAL_INFORMATION]: 'Financial information',
      [PROFILE_REWARDS_CATEGORIES.PURCHASE_HISTORY]: 'Purchase history',
      [PROFILE_REWARDS_CATEGORIES.DONATION_HISTORY]: 'Donation history',
      [PROFILE_REWARDS_CATEGORIES.CREDIT_CARD_INFORMATION]: 'Credit Cards',
      [PROFILE_REWARDS_CATEGORIES.INVESTMENT_HISTORY]: 'Investment history',
      [PROFILE_REWARDS_CATEGORIES.OTHER]: 'Other',
    },
    categoryEmailRelationship: ' — ',
    emptyFieldDash: '—',
    requiredDot: '*',
    editLabel: 'Edit',
    editModal: {
      invalidAnswerMessage: 'Please enter a valid answer',
      tooLongAnswerMessage: 'Please limit your answer to maximum of 100 characters.',
      updateFailedMessage: 'Something went wrong while updating the answer. Please try again later.',
      saveButton: 'Save',
    },
  },
  pendingNotificationModalType: {
    [PENDING_NOTIFICATION_MODAL_TYPE.REFERRAL_REFEREE_IN_PROGRESS]: {
      title: {
        part1: 'ONE MORE STEP TO GET ',
        part2: (points) => points,
        part3: '!',
      },
      description: (bonusAmount) =>
        `Thanks for joining Reklaim, join Profile Rewards to receive your ${bonusAmount} refer a friend bonus!`,
      buttonText: 'Join Profile Rewards',
    },
    [PENDING_NOTIFICATION_MODAL_TYPE.REFERRAL_REFERRER_BONUS_AMOUNT]: {
      title: {
        part1: 'A ',
        part2: (points) => points,
        part3: ' REFERRAL BONUS HAS BEEN ADDED TO YOUR BALANCE!',
      },
      description: 'Your friend has successfully signed up and joined Profile Rewards!',
      buttonText: 'Start earning more',
    },
    [PENDING_NOTIFICATION_MODAL_TYPE.REFERRAL_REFEREE_BONUS_AMOUNT]: {
      title: {
        part1: 'A ',
        part2: (points) => points,
        part3: ' REFERRAL BONUS HAS BEEN ADDED TO YOUR BALANCE!',
      },
      buttonText: 'Start earning more!',
    },
  },
  myFootprint: {
    fetchErrorMessage: 'Failed to load the data. Please revisit and try again later.',
    summarySection: {
      title: 'MY FOOTPRINT',
      numberOfBuyers: 'Number of buyers',
      numberOfBuyersToolTip: 'How many companies have purchased the data you have shared with us.',
      exposedPasswords: 'Exposed passwords',
      exposedPasswordsToolTip:
        'An exposed password is when we detect that one of your passwords has been breached from a service/company you signed up for.',
      valueDataPerYear: 'Big tech profits from your data',
      perYear: ' /YR',
      valueDataPerYearToolTip:
        'How much money large tech companies (Google, Amazon, Facebook, etc.) are making from collecting and selling your data on a per year basis.',
      scanButton: 'Scan for breached data',
    },

    dataBuyerSection: {
      title: "WHO'S BUYING MY DATA?",
      emptyDataMessage: 'There are no Data Buyers to display.',
    },
    dataSellerSection: {
      title: "WHO'S SELLING MY DATA?",
      emptyDataMessage: 'There are no Data Sellers to display.',
      byYear: ' /YR',
      learnMoreButton: 'Learn more',
      explainerModal: {
        title: (dataSellerName) => `How does ${dataSellerName} use your data?`,
        continueButton: 'Got it!',
      },
    },
    dataBreachSection: {
      title: 'WHERE HAS DATA BEEN BREACHED?',
      emptyDataMessage: 'There are no Data Breaches to display.',
      users: ' USERS',
      learnMoreButton: 'Learn more',
      explainerModal: {
        usersAffectedLabel: 'Users affected',
        dateLabel: 'Date',
        exposedDataTitle: 'EXPOSED DATA',
        continueButton: 'Got it!',
      },
    },
  },
  polls: {
    hero: {
      title: 'EARN FROM COMPLETING POLLS',
      description: 'You may need to pre-qualify for some polls',
    },
    unlockSurveysCard: {
      cardTitle: 'UNLOCK SURVEYS',
      cardDescription: 'Tell us a little bit about yourself to unlock surveys!',
      buttonText: 'Unlock surveys',
    },
    noPollsCard: {
      title: 'NO SURVEYS AVAILABLE',
      message: "You've completed all surveys and polls. Check back later for more.",
    },
    pollCard: {
      title: 'SURVEY',
      surveyLengthSingular: '1 minute',
      surveyLength: (time) => `${time} minutes`,
      startButton: 'Start',
    },
    surveyScreen: {
      progressRemaining: (total) => ` /${total}`,
      textQuestionPlaceholder: 'Enter your answer',
      multiSelectQuestionDescription: 'Select all that apply',
      nextQuestion: 'Next',
      completeSurvey: 'Complete',
    },
    surveyCompletionScreen: {
      title: {
        part1: 'CONGRATULATIONS',
        part2: "YOU'VE EARNED",
      },
      message:
        'Each answer gets you closer and closer to one of our redemption options. Your balance will be updated soon',
      learnButton: 'Learn how earnings work',
      continueButton: 'Keep going',
    },
    screenOutModal: {
      title: 'WELL THIS IS AWKWARD',
      message: "Unfortunately you don't qualify for that survey, but don't fret! There are so many more.",
      continueButton: 'Keep going',
    },
    payableScreenOutModal: {
      title: (amountFormatted) => `YOU'VE EARNED ${amountFormatted}`,
      message: "You won't always qualify for all surveys, but we'll still pay you for your time!",
      continueButton: 'Keep going',
    },
  },
  subscription: {
    onlyAvailableOnApp: 'ONLY AVAILABLE ON THE APP',
    getDownloadLinkButton: 'Email me a download link',
    sent: 'Sent!',
    continue: 'Continue',
  },
  privacyCard: {
    title: 'UNLOCK YOUR BREACHED DATA AND MUCH MORE.',
    buttonText: 'Subscribe',
  },
  earnFromDataCard: {
    description: 'START WITH OUR MOST POPULAR WAYS TO EARN.',
    buttonText: 'Earn more from my data',
  },
  earnMoreFlow: {
    header: {
      title: 'Start with our most popular ways to earn!',
      screenCountDenominator: (denominator) => `/${denominator}`,
    },
    profileRewardsScreen: {
      pointsEarningText: (pointsEarningValue) => `${pointsEarningValue} /wk`,
      title: {
        text1: 'EARN FROM YOUR ',
        highlight: 'PROFILE ',
        text2: 'DATA',
      },
      answerField: {
        postalCode: 'ZIP',
        dateOfBirth: 'Date of birth',
        gender: 'Gender',
      },
      shareButton: 'Share',
      skipButton: "I'll do this later",
      shareFailedText: 'Could not share your profile data at this time. Please try again later.',
    },
  },
  pollsExplainerScreen: {
    title: {
      text: 'EARN FROM COMPLETING ',
      highlight: 'POLLS',
    },
    options: {
      unlock: {
        title: 'Unlock polls',
        subtitle: 'Tell us a little bit about yourself in our intro survey.',
      },
      complete: {
        title: 'Complete polls',
        subtitle: 'Over 100 polls for you to earn from.',
      },
      earn: {
        title: 'Earn instantly',
        subtitle: 'Instantly get 1 PT in your account for each question you answer.',
      },
    },
    continueButton: 'Unlock Polls',
    cancelButton: "I'll do this later",
  },
  signUpFork: {
    privacyExplainer: {
      title: 'PROTECT YOUR DIGITAL PRIVACY.',
      scanData: {
        label: 'Scan for your data',
        description: "We'll scan for your exposed data on the web.",
      },
      stayUpdated: {
        label: 'Stay updated',
        description: 'Get notified when we detect your data in new breaches.',
      },
      blockTrackers: {
        label: 'Block trackers',
        description: 'Your internet traffic will be encrypted and untrackable.',
      },
      continueButton: 'Get started',
    },
    compensationExplainer: {
      title: 'GET COMPENSATED FOR YOUR DATA.',
      earnWeekly: {
        label: 'Earn weekly',
        description: (maxWeeklyEarningPoint, maxWeeklyEarningDollar) =>
          `Share your data for up to ${maxWeeklyEarningPoint} ($${maxWeeklyEarningDollar}) a week.`,
      },
      earnNow: {
        label: 'Earn now',
        description: 'Answer polls to get PTS right away.',
      },
      redeemYourPoints: {
        label: 'Redeem your points',
        description: (redemptionMinBalance) => `Redeem once you have a balance of ${redemptionMinBalance}.`,
      },
      tonsOfRewards: {
        label: 'Tons of rewards',
        description: 'Giftcards, BTC, ETH with more coming!',
      },
      continueButton: 'Get started',
    },
  },
  claimPaycheckModal: {
    claimPaycheckTitle: 'CLAIM YOUR PAYCHECK NOW!',
    claimPaycheckDescription: (qty) => `Claim your paycheck worth ${qty} now before it expires!`,
    claimingPaycheckTitle: 'CLAIMING PAYCHECK',
    claimedPaycheckTitle: 'SUCCESS!',
    claimedPaycheckDescription:
      'Your paycheck was deposited in your Reklaim account. Check back next week for your next payment.',
    buttonTextClose: 'Close',
    buttonTextClaim: 'Claim now',
  },
  // Debug Only
  designSystemScreen: {
    typography: 'Typography',
    colours: 'Colours',
    inputs: 'Inputs',
    buttons: 'Buttons',
    label: 'Design System',
    titleBold: 'Druk Bold Text',
    boldText: 'Garamond Bold Text',
    boldItalicText: 'Garamond Bold Italic text',
    mediumText: 'Garamond Medium Text',
    mediumItalic: 'Garamond Medium Italic Text',
    semiBold: 'Garamond Semi Bold Text',
    semiBoldItalic: 'Garamond Semi Bold Italic Text',
    extraBold: 'Garamond Extra Bold Text',
    extraBoldItalic: 'Garamond Extra Bold Italic Text',
    regularText: 'Garamond Regular Text',
    italicText: 'Garamond Italic Text',
  },
}

export default STRING
